// dashboard/components/Notifications.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive,
  NotificationsOff,
  CheckCircle,
  Warning,
  Info,
  Error,
  Delete,
  DoneAll,
  Settings,
  MoreVert,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const NotificationItem = styled(ListItem)(({ theme, read }) => ({
  backgroundColor: read ? 'transparent' : 'rgba(52, 152, 219, 0.05)',
  borderLeft: read ? '3px solid transparent' : '3px solid #3498db',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
}));

const Notifications = () => {
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const notifications = [
    {
      id: 1,
      type: 'success',
      title: 'Payment Successful',
      message: 'Your payment of $299.99 for "Advanced Forex Trading Course" was successful.',
      time: '2 hours ago',
      read: false,
    },
    {
      id: 2,
      type: 'warning',
      title: 'Subscription Expiring Soon',
      message: 'Your VIP Telegram Signals subscription will expire in 5 days. Renew now to avoid interruption.',
      time: '1 day ago',
      read: false,
    },
    {
      id: 3,
      type: 'info',
      title: 'New Course Available',
      message: 'Check out our new course "Algorithmic Trading with Python" - Enroll now with 20% off!',
      time: '2 days ago',
      read: true,
    },
    {
      id: 4,
      type: 'success',
      title: 'Signal Alert',
      message: 'New buy signal for EUR/USD at 1.08750 with 85% confidence.',
      time: '3 days ago',
      read: true,
    },
    {
      id: 5,
      type: 'error',
      title: 'Payment Failed',
      message: 'Your recent payment of $149.99 for "Options Trading 101" failed. Please update your payment method.',
      time: '5 days ago',
      read: true,
    },
    {
      id: 6,
      type: 'info',
      title: 'Webinar Reminder',
      message: 'Don\'t forget to join our live webinar "Advanced Trading Strategies" tomorrow at 3 PM EST.',
      time: '1 week ago',
      read: true,
    },
  ];

  const getTypeIcon = (type) => {
    switch(type) {
      case 'success': return <CheckCircle sx={{ color: '#2ecc71' }} />;
      case 'warning': return <Warning sx={{ color: '#f39c12' }} />;
      case 'error': return <Error sx={{ color: '#e74c3c' }} />;
      case 'info': return <Info sx={{ color: '#3498db' }} />;
      default: return <Info sx={{ color: '#3498db' }} />;
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'success': return '#2ecc71';
      case 'warning': return '#f39c12';
      case 'error': return '#e74c3c';
      case 'info': return '#3498db';
      default: return '#3498db';
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (tabValue === 0) return true;
    if (tabValue === 1) return !n.read;
    if (tabValue === 2) return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAllRead = () => {
    // Mark all as read logic
  };

  const handleDeleteAll = () => {
    // Delete all notifications logic
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0a1628' }}>
            Notifications
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d', mt: 0.5 }}>
            Stay updated with your latest activity
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DoneAll />}
            onClick={handleMarkAllRead}
            sx={{ borderRadius: 2, borderColor: '#eef2f7' }}
          >
            Mark All Read
          </Button>
          <Button
            variant="outlined"
            startIcon={<Delete />}
            onClick={handleDeleteAll}
            sx={{ borderRadius: 2, borderColor: '#eef2f7', color: '#e74c3c' }}
          >
            Delete All
          </Button>
          <IconButton onClick={handleMenuOpen} sx={{ color: '#7f8c8d' }}>
            <MoreVert />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #eef2f7', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
            },
            '& .Mui-selected': {
              color: '#2ecc71',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#2ecc71',
            },
          }}
        >
          <Tab label="All" />
          <Tab 
            label={
              <Badge badgeContent={unreadCount} color="error">
                Unread
              </Badge>
            } 
          />
          <Tab label="Read" />
        </Tabs>
      </Paper>

      {/* Notifications List */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #eef2f7' }}>
        <List>
          {filteredNotifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <NotificationsOff sx={{ fontSize: 48, color: '#95a5a6' }} />
              <Typography variant="h6" sx={{ color: '#0a1628', mt: 2 }}>
                No notifications
              </Typography>
              <Typography variant="body2" sx={{ color: '#95a5a6' }}>
                You're all caught up!
              </Typography>
            </Box>
          ) : (
            filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <NotificationItem read={notification.read}>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: `${getTypeColor(notification.type)}20`, width: 40, height: 40 }}>
                      {getTypeIcon(notification.type)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#0a1628' }}>
                          {notification.title}
                        </Typography>
                        {!notification.read && (
                          <Chip
                            label="New"
                            size="small"
                            sx={{
                              bgcolor: '#3498db',
                              color: '#fff',
                              height: 20,
                              fontSize: '10px',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ color: '#4a4a5a', mb: 0.5 }}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#95a5a6' }}>
                          {notification.time}
                        </Typography>
                      </Box>
                    }
                  />
                  <IconButton size="small" sx={{ color: '#95a5a6' }}>
                    <Delete sx={{ fontSize: 20 }} />
                  </IconButton>
                </NotificationItem>
                {index < filteredNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>

      {/* Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            mt: 1,
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <NotificationsActive sx={{ mr: 1, fontSize: 20 }} />
          Enable Push Notifications
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Settings sx={{ mr: 1, fontSize: 20 }} />
          Notification Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: '#e74c3c' }}>
          <Delete sx={{ mr: 1, fontSize: 20 }} />
          Clear All
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Notifications;